// Pane → Interactions → one cab: its KPIs and its ride timeline. Each ride (an occupancy) carries its panes in device order; each pane
// opens into its trail — the journey events in pane_seq order (journey-events-receipt-order.md: the lake receives them out of order, the
// device numbered them in order). A ride is a door (?ride=<occupancy id> scrolls to it and marks it); a tenant is a door to its page; the
// cab is a door to its Device ledger and its Fleet pane state. Read: GET /api/v2/pane/interactions/cabs/:thingName.
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, Link2 } from 'lucide-react';
import { PageHead, Tiles, Loading, ErrorState, Empty, Button } from '../../components/ui';
import useApi from '../../lib/useApi';
import api from '../../../services/api';
import ob from '../fleet/Onboarding.module.css';
import { errMsg } from './lib';
import {
  useWindow, FilterBar, tenantLabel, tenantKind, thingShort, num, pct, dur, durShort, clock, clockS, dayTime, causeWords, groupOf, groupColor, screensWords, ix as s,
} from './interactionsLib';

const read = (p) => p.then((r) => r.data.data).catch((e) => { throw new Error(errMsg(e)); });

function eventWords(e) {
  switch (e.type) {
    case 'pane_opened': return 'Pane opened';
    case 'tenant_mounted': return 'App mounted in the pane';
    case 'tenant_event': return e.name === 'screen' ? `Screen → ${e.v}` : `${e.name || 'event'}${e.v ? ` · ${e.v}` : ''}`;
    case 'screen_shown': return `Page screen → ${e.screen}`;
    case 'category_selected': return `Category chosen · ${e.category_id || '—'}`;
    case 'card_viewed': return `Card seen · ${e.package_id || '—'}`;
    case 'card_dwell': return `Card dwelt on · ${e.package_id || '—'}`;
    case 'detail_viewed': return `Details opened · ${e.package_id || '—'}`;
    case 'cta_tapped': return `Tapped the call to action · ${e.package_id || '—'}`;
    case 'qr_shown': return `QR shown${e.outcome ? ` (${e.outcome})` : ''}`;
    case 'callback_started': return 'Callback started';
    case 'nav_woken': return 'Touched the nav strip';
    case 'journey_wiped': return 'Cleared by the passenger';
    case 'tenant_detached': return `App detached${e.visible_ms != null ? ` — ${dur(e.visible_ms)} visible` : ''}`;
    case 'pane_closed': return `Pane closed — ${causeWords(e.cause).toLowerCase()}`;
    default: return e.type;
  }
}

function PaneRow({ p, map, win }) {
  const [open, setOpen] = useState(false);
  const g = groupOf(p.close_cause, map);
  const bits = [p.steps ? `${p.steps} in-app ${p.steps === 1 ? 'event' : 'events'}` : null, p.strip_touches ? `${p.strip_touches} strip ${p.strip_touches === 1 ? 'touch' : 'touches'}` : null,
    p.clears ? 'cleared' : null, p.scans ? `${p.scans} ${p.scans === 1 ? 'scan' : 'scans'}` : null].filter(Boolean);
  return (<>
    <button type="button" className={s.paneRow} onClick={() => setOpen((v) => !v)} aria-expanded={open} data-pane={p.pane_id}>
      <span className={s.paneTime}>{clock(p.opened_ms)}</span>
      <span className={s.paneTenant}><Link to={`/v2/pane/interactions/tenant/${encodeURIComponent(p.tenant_id)}${win.linkQs()}`} onClick={(e) => e.stopPropagation()}>{tenantLabel(p.tenant_id)}</Link><small>{tenantKind(p.tenant_id, p.tenant_kind)}</small></span>
      <span className={s.paneDur}>{p.span_ms != null ? dur(p.span_ms) : '—'}{p.visible_ms != null && <small>{dur(p.visible_ms)} visible</small>}</span>
      <span className={s.paneClose} title={p.close_cause || 'no pane_closed row'}><i className={s.swatch} style={{ background: groupColor(g) }} />{causeWords(p.close_cause)}</span>
      <span className={s.paneScreens}>{p.screens?.length ? screensWords(p.screens) : <small>no in-app screen reported</small>}{bits.length ? <small> · {bits.join(' · ')}</small> : null}</span>
      <span className={s.chev}>{open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</span>
    </button>
    {open && (
      <ol className={s.trail} aria-label="The pane's events in device order">
        {(p.events || []).map((e, i) => <li key={`${e.seq}-${i}`}><span>{clockS(e.at_ms)}</span><span>{e.seq ?? ''}</span><span>{eventWords(e)}</span></li>)}
      </ol>)}
  </>);
}

function RideCard({ ride, panes, map, win, on }) {
  const ref = React.useRef(null);
  useEffect(() => { if (on && ref.current) ref.current.scrollIntoView({ block: 'start' }); }, [on]);
  const meta = [ride.ended ? dur(ride.duration_ms) : 'in progress', ride.touches != null ? `${ride.touches} ${ride.touches === 1 ? 'touch' : 'touches'}` : null,
    ride.origin ? `started on ${ride.origin === 'presence' ? 'camera presence' : 'a touch'}` : null, `${panes.length} ${panes.length === 1 ? 'pane' : 'panes'}`,
    ride.scans ? `${ride.scans} ${ride.scans === 1 ? 'scan' : 'scans'}` : null, ride.before_window ? 'started before this window' : null].filter(Boolean);
  return (
    <section ref={ref} className={`${s.ride} ${on ? s.rideOn : ''}`} id={`ride-${ride.occupancy_id}`} data-ride={ride.occupancy_id}>
      <div className={s.rideHead}>
        <span className={s.rideTitle}>Ride · {ride.started_ms ? `${dayTime(ride.started_ms)} IST` : 'start not recorded'}</span>
        <span className={s.rideMeta}>{meta.join(' · ')}</span>
        <Link className={s.rideLink} to={`?${new URLSearchParams({ ...Object.fromEntries(new URLSearchParams(win.linkQs())), ride: ride.occupancy_id })}`} title="A link to this ride"><Link2 size={12} style={{ verticalAlign: -1 }} /> link</Link>
      </div>
      {panes.length ? panes.map((p) => <PaneRow key={p.pane_id} p={p} map={map} win={win} />) : <div className={s.noPanes}>No pane opened in this ride.</div>}
    </section>);
}

export default function InteractionsCab() {
  const { thingName } = useParams();
  const [params] = useSearchParams();
  const win = useWindow();
  const focus = params.get('ride');
  const apiParams = { ...win.apiParams }; delete apiParams.bench;   // a named cab is always read; the bench toggle is a fleet filter
  const q = useApi(() => read(api.get(`/v2/pane/interactions/cabs/${encodeURIComponent(thingName)}`, { params: apiParams })), [thingName, JSON.stringify(apiParams)]);
  const d = q.data;
  const groups = useMemo(() => {
    if (!d) return null;
    const byRide = new Map(); (d.panes || []).forEach((p) => { if (!byRide.has(p.occupancy_id)) byRide.set(p.occupancy_id, []); byRide.get(p.occupancy_id).push(p); });
    const known = new Set((d.rides || []).map((r) => r.occupancy_id));
    const withPanes = (d.rides || []).filter((r) => byRide.has(r.occupancy_id));
    const quiet = (d.rides || []).filter((r) => !byRide.has(r.occupancy_id));
    const orphan = (d.panes || []).filter((p) => !known.has(p.occupancy_id));
    return { byRide, withPanes, quiet, orphan };
  }, [d]);
  const head = (
    <>
      <div className={s.crumbs}><Link to={`/v2/pane/interactions/cabs${win.linkQs()}`}>← Interactions · Cabs</Link></div>
      <PageHead title={<>{thingShort(thingName)}{d?.cab?.bench && <span className={s.tag}>Bench</span>}</>}
        sub={<>{thingName} — every ride in the window and the panes opened in it, in the cab's own order. Times IST.</>}
        actions={<Button onClick={q.reload}>Refresh</Button>} />
      <div className={s.doors} style={{ margin: '-8px 0 16px' }}><Link className={ob.btnTool} to={`/v2/fleet/device/${encodeURIComponent(thingName)}`}>Device ledger →</Link>
        <Link className={ob.btnTool} to={`/v2/pane/fleet?thing=${encodeURIComponent(thingName)}`}>Fleet pane state →</Link></div>
    </>);
  if (q.loading && !d) return <div className={ob.wrap}>{head}<Loading what="the cab's rides" /></div>;
  if (q.error) return <div className={ob.wrap}>{head}<ErrorState error={q.error} onRetry={q.reload} /></div>;
  if (!d) return null;
  const k = d.kpis || {};
  return (
    <div className={ob.wrap}>
      {head}
      <FilterBar win={win} data={d} ob={ob} showBench={false} showTenants={false} />
      <Tiles min={140} items={[
        { label: 'Rides', value: num(d.rides_total), foot: `${num(k.rides_touched || 0)} touched · ${num(k.rides_with_pane || 0)} with a pane` },
        { label: 'Panes opened', value: num(k.panes || 0), foot: k.top_tenant ? `most: ${tenantLabel(k.top_tenant)} (${k.top_tenant_panes})` : 'none' },
        { label: 'Time in panes', value: k.panes ? durShort(k.time_in_pane_ms) : '—', foot: k.visible_ms != null ? `${dur(k.visible_ms)} of it inside an app` : ' ' },
        { label: 'Closed by idle', value: num(k.idle_closes || 0), tone: k.idle_closes ? 'warn' : undefined, foot: k.panes ? `${pct(k.idle_closes || 0, k.panes)} of panes` : '—' },
        { label: 'Clears', value: num(k.clears || 0), foot: 'the passenger\'s Clear' },
        { label: 'QR scans', value: num(k.scans || 0), foot: ' ' },
        { label: 'Last interaction', value: k.last_interaction_ms ? clock(k.last_interaction_ms) : '—', foot: k.last_interaction_ms ? dayTime(k.last_interaction_ms).replace(/,? \d\d:\d\d$/, '') : 'none in this window' },
      ]} />
      <h2 className={s.sectionTitle}>Rides with a pane <span style={{ font: '400 12.5px var(--f-body)', color: 'var(--ink-mute)' }}>— newest first; open a pane to read its events</span></h2>
      {groups.withPanes.length === 0 && !groups.orphan.length && <Empty title="No pane opened in this window">The rides below started, but nobody opened a pane.</Empty>}
      {groups.withPanes.map((r) => <RideCard key={r.occupancy_id} ride={r} panes={groups.byRide.get(r.occupancy_id)} map={d.cause_group} win={win} on={focus === r.occupancy_id} />)}
      {groups.orphan.length > 0 && (
        <section className={s.ride}><div className={s.rideHead}><span className={s.rideTitle}>Panes whose ride is not in the lake</span><span className={s.rideMeta}>their occupancy has no start or end row in this window ± 1 day</span></div>
          {groups.orphan.map((p) => <PaneRow key={p.pane_id} p={p} map={d.cause_group} win={win} />)}</section>)}
      {groups.quiet.length > 0 && (
        <details className={s.quietRides} open={groups.quiet.some((r) => r.occupancy_id === focus)}>
          <summary>{groups.quiet.length} {groups.quiet.length === 1 ? 'ride' : 'rides'} without a pane — {groups.quiet.filter((r) => r.touches > 0).length} of them touched the screen</summary>
          <div className={s.quietList}>
            {groups.quiet.map((r) => (
              <Link key={r.occupancy_id} id={`ride-${r.occupancy_id}`} to={`?${new URLSearchParams({ ...Object.fromEntries(new URLSearchParams(win.linkQs())), ride: r.occupancy_id })}`}
                style={focus === r.occupancy_id ? { color: 'var(--iris-text)', fontWeight: 600 } : undefined}>
                {r.started_ms ? dayTime(r.started_ms) : '—'} · {r.ended ? dur(r.duration_ms) : 'in progress'} · {r.touches != null ? `${r.touches} touches` : '—'} · {r.origin || '—'}
              </Link>))}
          </div>
        </details>)}
      {d.rides_total > d.ride_cap && <p className={s.cardNote}>Showing the {d.ride_cap} newest rides of {d.rides_total}; narrow the window to read older ones.</p>}
    </div>);
}
